import React from 'react';
import {
    Box,
    Button,
    Text,
    Flex,
    Collapse,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    ModalFooter,
    Tag
} from '@chakra-ui/react';
import { FiActivity, FiChevronDown, FiChevronUp } from 'react-icons/fi';

// The API returns an audit trail on `logs`: { username, status, timestamp }.
export const logStatusColor = (status) => {
    const s = (status || '').toLowerCase();
    if (s.includes('cancel') || s.includes('fail') || s.includes('error')) return 'red';
    if (s.includes('complet') || s.includes('success')) return 'green';
    if (s.includes('pause') || s.includes('stop')) return 'yellow';
    if (s.includes('start') || s.includes('resum') || s.includes('process')) return 'orange';
    if (s.includes('upload')) return 'blue';
    return 'gray';
};

export const formatLogTime = (timestamp) => {
    if (!timestamp) return '—';
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return String(timestamp);
    return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
};

// Vertical timeline of audit-trail entries, oldest first.
export function ActivityTimeline({ logs, maxH = '240px', emptyText = 'No activity yet…' }) {
    const items = Array.isArray(logs) ? logs : [];

    if (items.length === 0) {
        return <Text fontSize="xs" color="gray.500" p={3} textAlign="center">{emptyText}</Text>;
    }

    return (
        <Box maxH={maxH} overflowY="auto">
            {items.map((log, idx) => {
                const isLast = idx === items.length - 1;
                return (
                    <Flex key={log._id || idx} gap={3} align="stretch">
                        <Flex direction="column" align="center" flexShrink={0}>
                            <Box
                                w="10px" h="10px" mt="5px" borderRadius="full"
                                bg={`${logStatusColor(log.status)}.500`}
                            />
                            {!isLast && <Box flex={1} w="2px" minH="20px" bg="gray.200" />}
                        </Flex>
                        <Box flex={1} pb={isLast ? 0 : 4}>
                            <Flex align="center" gap={2} flexWrap="wrap">
                                <Text fontSize="sm" fontWeight="medium">{log.status || '—'}</Text>
                                <Tag size="sm" colorScheme={logStatusColor(log.status)} fontSize="xs">
                                    {log.username || 'System'}
                                </Tag>
                            </Flex>
                            <Text fontSize="xs" color="gray.500" mt={0.5}>
                                {formatLogTime(log.timestamp)}
                            </Text>
                        </Box>
                    </Flex>
                );
            })}
        </Box>
    );
}

// Collapsible card used inside the campaign wizard.
export function CampaignActivityLog({ logs, isExpanded, onToggle }) {
    const count = Array.isArray(logs) ? logs.length : 0;

    return (
        <Box className='box-style-card'>
            <Flex justify="space-between" align="center" cursor="pointer" onClick={onToggle}>
                <Flex align="center" gap={2}>
                    <FiActivity color="var(--brand-primary)" />
                    <Text fontSize="lg" fontWeight={400}>Activity Log</Text>
                    {count > 0 && <Tag size="sm" colorScheme="gray">{count}</Tag>}
                </Flex>
                {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
            </Flex>
            <Collapse in={isExpanded}>
                <Box mt={3}>
                    <ActivityTimeline logs={logs} />
                </Box>
            </Collapse>
        </Box>
    );
}

// Standalone modal opened from the campaign list.
export function CampaignLogsModal({ isOpen, onClose, campaign }) {
    const logs = Array.isArray(campaign?.logs) ? campaign.logs : [];
    const title = campaign?.name
        ? campaign.name.replace(/^csv-\d+-\d+\.csv$/, 'Campaign File').replace('.csv', '')
        : 'Campaign';

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside" isCentered>
            <ModalOverlay />
            <ModalContent maxH="85vh">
                <ModalHeader>
                    <Flex align="center" gap={2}>
                        <FiActivity size={20} color="var(--brand-primary)" />
                        <Text>Activity Log</Text>
                    </Flex>
                    <Text fontSize="sm" fontWeight="normal" color="gray.600" mt={1}>
                        {title}
                    </Text>
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <ActivityTimeline
                        logs={logs}
                        maxH="none"
                        emptyText="No activity recorded for this campaign."
                    />
                </ModalBody>
                <ModalFooter borderTop="1px" borderColor="gray.200">
                    <Button variant="outline" onClick={onClose}>Close</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
